import React, { useEffect, Suspense } from 'react';
// Every page loads its code on first open; this lazy() survives the file having been replaced by a
// newer deploy (or a restarted dev server) instead of crashing the app — see utils/lazyWithRetry.
import { lazyWithRetry as lazy } from './utils/lazyWithRetry';
import { bearriPathFor } from './components/bearri/dealStatus';
import { scoutPathFor } from './components/bearri/scout/scoutModel';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import MainLayout from './layouts/MainLayout';
import PublicLayout from './layouts/PublicLayout';
import { ScrollToTop } from './components/ScrollToTop';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { purgeLegacyStorage } from './utils/legacyStorage';
import { Toaster } from './components/ui/shadcn/sonner';
import { NectarLoader } from './components/hive';
// /signup is retired (invite-only): a plain redirect, loaded with the app so it never flashes a loader
import SignupRedirect from './pages/auth/SignupRedirect';

// Dashboard stays eager — it's the authed landing route and we want fastest
// TTI there. Everything else is code-split so the public `/` bundle stays
// tight.
import Dashboard from './pages/Dashboard';

// Non-critical routes are code-split to keep the main bundle small.
const SponsorIntel = lazy(() => import('./pages/SponsorIntel'));
const About = lazy(() => import('./pages/About'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Landing = lazy(() => import('./pages/Landing'));
const Profile = lazy(() => import('./pages/Profile'));
const DealRoom = lazy(() => import('./pages/DealRoom'));
// Development only: lets the deal room be reviewed without an account and a
// live Gmail thread. Never registered in a production build.
const Bearri = lazy(() => import('./pages/Bearri'));
// Development-only previews. The ternary sits around each import() so a production build drops
// the import — and the chunk — entirely; a lazy() outside it shipped every preview as a dead file.
const DevNothing = (() => null) as React.ComponentType<any>;
const DealRoomPreview = process.env.NODE_ENV === 'development' ? lazy(() => import('./pages/DealRoomPreview')) : DevNothing;
const WorkspacePreview = process.env.NODE_ENV === 'development' ? lazy(() => import('./pages/WorkspacePreview')) : DevNothing;
// Development only: real signed-in pages at their real paths, signed out (see DevPagePreview).
const DevPagePreview = process.env.NODE_ENV === 'development' ? lazy(() => import('./pages/DevPagePreview')) : DevNothing;
const DeskPreview = process.env.NODE_ENV === 'development' ? lazy(() => import('./pages/DeskPreview')) : DevNothing;
const BearriPreview = process.env.NODE_ENV === 'development' ? lazy(() => import('./pages/BearriPreview')) : DevNothing;
const BackgroundActivity = lazy(() => import('./pages/BackgroundActivity'));
const PitchWriter = lazy(() => import('./pages/PitchWriter'));
const CreatorProfile = lazy(() => import('./pages/CreatorProfile'));
// The auth doors (src/pages/auth): sign in (night room), the waitlist and the invite landing (dawn room).
const SignInPage = lazy(() => import('./pages/auth/SignInPage'));
const WaitlistPage = lazy(() => import('./pages/auth/WaitlistPage'));
const InvitePage = lazy(() => import('./pages/auth/InvitePage'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'));
// Plan & billing: the one home for a creator's plan (/app/pricing and /app/billing land here).
const Plan = lazy(() => import('./pages/Plan'));
// Public share pages — rendered OUTSIDE AppGuard so unauthenticated sponsors
// can open the link without bouncing through Clerk login.
const PublicReport = lazy(() => import('./pages/PublicReport'));
// The deal link a creator sends a brand: no account, no chrome, noindex.
const PublicDeal = lazy(() => import('./pages/PublicDeal'));

// Note: Discover page has been refactored to SponsorIntel (offer-centric view).

/**
 * Smart root: signed-in users land on their dashboard, signed-out see the
 * marketing landing. `/` is the public marketing page; the authed app lives
 * under `/app/*` (Sprint 6 route refactor).
 */
const SmartRoot: React.FC = () => {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return null;
  if (isSignedIn) return <Navigate to="/app/dashboard" replace />;
  return <Landing />;
};

/**
 * Guard for anything under `/app/*`. If the user isn't signed in, bounce to
 * `/login?next=<original>` so post-sign-in can land them back where they
 * tried to go.
 */
const AppGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();
  if (!isLoaded) return null;
  if (!isSignedIn) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <>{children}</>;
};

/**
 * Catch-all for unknown paths: authed → /app/dashboard, otherwise → /.
 * Split out so we can use the `useAuth` hook without tripping React's
 * rules-of-hooks inside the Routes array.
 */
const SmartCatchAll: React.FC = () => {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return null;
  return <Navigate to={isSignedIn ? '/app/dashboard' : '/'} replace />;
};

/**
 * Auth-aware /about wrapper. Signed-in users see the page inside the full
 * authed MainLayout (rich nav, footer). Signed-out users
 * see it inside PublicLayout (public top nav + minimal footer). Same
 * `<About />` component either way — the chrome is what differs.
 */
const AboutWrapper: React.FC = () => {
  const { isSignedIn, isLoaded } = useAuth();
  if (!isLoaded) return null;
  if (isSignedIn) {
    return (
      <SubscriptionProvider>
        <MainLayout>
          <About />
        </MainLayout>
      </SubscriptionProvider>
    );
  }
  return (
    <PublicLayout>
      {/* Paper chrome: About manages its own width here — a full-bleed painted
          shadowbox head, then a paper body at the landing's content width. */}
      <About chrome="paper" />
    </PublicLayout>
  );
};

/**
 * /pricing: signed in → Plan & billing (the query kept, so ?choose= still picks a plan); signed out → the landing's
 * pricing (/#pricing). The SmartCatchAll pattern.
 */
const PricingDoor: React.FC = () => {
  const { isSignedIn, isLoaded } = useAuth();
  const { search } = useLocation();
  if (!isLoaded) return null;
  return <Navigate to={isSignedIn ? `/app/plan${search}` : '/#pricing'} replace />;
};

function RedirectKeepQuery({ to }: { to: string }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

/** The old Deals list (/app/outreach?pitch=) and the old assistant page (/app/assistant?thread=, ?deal=)
 *  both open the Bearri tab on the same deal or conversation — reply-alert emails and job links use them. */
function ToBearri() {
  const { search } = useLocation();
  return <Navigate to={bearriPathFor(search)} replace />;
}

/** The old Brand Scout page (/app/prospects): ?run=<id> reopens that shortlist in Bearri, anything else opens the scout order. */
function ToScout() {
  const { search } = useLocation();
  return <Navigate to={scoutPathFor(search)} replace />;
}

/** The old per-code report link. Per-code reports are retired; the code itself is in the dashboard's sponsors. */
function CodeReportRedirect() {
  return <Navigate to="/app/dashboard#sponsors" replace />;
}

/**
 * Old sponsor-facing report links (/share/pcr/*, /share/case-study/*). Those reports were built from
 * purchase records Bearri no longer uses, so the link says plainly that it is gone instead of
 * dropping a brand on the marketing page. No figures of any kind.
 */
function RetiredReportLink() {
  useEffect(() => {
    document.title = 'Report no longer available · Bearri';
  }, []);
  return (
    <main className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#f4f1ed] px-4 text-center">
      <img src="/logo.png" alt="Bearri" className="h-10 w-auto" />
      <p className="max-w-md text-body text-slate-800">
        This report link is no longer available. Ask the creator for a current link.
      </p>
    </main>
  );
}

function App() {
  const { isLoaded } = useAuth();

  // Bearri no longer has a browser extension. Its old bridge left the session token and profile in
  // localStorage; clear them once per load so nothing lingers in anyone's browser.
  useEffect(() => {
    purgeLegacyStorage();
  }, []);

  // Use the logo for the favicon and document title
  useEffect(() => {
    document.title = 'Bearri — Sponsorships, landed.';

    const link =
      (document.querySelector("link[rel~='icon']") as HTMLLinkElement) ||
      document.createElement('link');
    // Bearri bear logo. Was pointing at /logo.svg, which never existed in
    // public/ → the browser fell back to the default favicon. /logo.png is
    // the real Bearri mark.
    link.type = 'image/png';
    link.rel = 'icon';
    link.href = '/logo.png';
    document.head.appendChild(link);
  }, []);

  // Before auth is known: the brand's own mark on the desk's colour and one quiet track.
  // (It was the old pink "thinking" bear on a blue-to-pink gradient — a character and a
  // palette the app itself no longer uses, shown first to everyone.)
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-5 bg-[#f4f1ed]">
        <img src="/logo.png" alt="" className="h-14 w-auto" />
        <NectarLoader size="lg" art={false} label="Loading Bearri" />
      </div>
    );
  }

  // While a public page's code loads. Signed-in pages have their own boundary inside the
  // workspace shell (below), so the header and folder tabs never blink out on a tab switch.
  const routeFallback = (
    <div className="min-h-[60vh] flex items-center justify-center">
      <NectarLoader size="lg" art={false} label="Loading page" />
    </div>
  );

  return (
    <Router>
      <ScrollToTop />
      <Toaster />
      <Suspense fallback={routeFallback}>
        <Routes>
          {/* ===================== PUBLIC ROUTES ===================== */}

          {/* Marketing landing — SmartRoot redirects authed users to /app */}
          <Route path="/" element={<SmartRoot />} />

          {/* About is public to everyone; authed users see it wrapped in
              MainLayout (full nav), signed-out users see it wrapped in
              PublicLayout (simple top nav). */}
          <Route path="/about" element={<AboutWrapper />} />

          {/* Legal — public, always in the simple PublicLayout so they're
              reachable signed-out (required by Google OAuth verification
              and Facebook Login). */}
          <Route path="/privacy" element={<PublicLayout><Privacy /></PublicLayout>} />
          <Route path="/terms" element={<PublicLayout><Terms /></PublicLayout>} />

          {/* Public share pages — sponsor-facing links the creator sends out.
              Rendered without any auth gate or layout chrome so a brand
              decision-maker can open the link cold from email. */}
          {/* Retired report links: a plain "no longer available" notice. */}
          <Route path="/share/pcr/:token" element={<RetiredReportLink />} />
          <Route path="/share/case-study/:token" element={<RetiredReportLink />} />
          <Route path="/share/report/:token" element={<PublicReport />} />
          <Route path="/deal/:token" element={<PublicDeal />} />

          {/* Development-only previews */}
          {process.env.NODE_ENV === 'development' && (
            <Route path="/dev/deal-room" element={<DealRoomPreview />} />
          )}
          {process.env.NODE_ENV === 'development' && (
            <Route path="/dev/workspace" element={<WorkspacePreview />} />
          )}
          {process.env.NODE_ENV === 'development' && <>
            <Route path="/dev/page/discover" element={<DevPagePreview path="/app/discover"><SponsorIntel /></DevPagePreview>} />
            <Route path="/dev/page/bearri" element={<DevPagePreview path="/app/bearri" keepUrl><Bearri /></DevPagePreview>} />
            <Route path="/dev/page/bearri-sample" element={<DevPagePreview path="/app/bearri" keepUrl><BearriPreview /></DevPagePreview>} />
            <Route path="/dev/page/desk-sample" element={<DevPagePreview path="/app/dashboard"><DeskPreview /></DevPagePreview>} />
            <Route path="/dev/page/dashboard" element={<DevPagePreview path="/app/dashboard"><Dashboard /></DevPagePreview>} />
            <Route path="/dev/page/pitch-writer" element={<DevPagePreview path="/app/pitch-writer"><PitchWriter /></DevPagePreview>} />
            <Route path="/dev/page/profile" element={<DevPagePreview path="/app/profile"><Profile /></DevPagePreview>} />
            <Route path="/dev/page/plan" element={<DevPagePreview path="/app/plan"><Plan /></DevPagePreview>} />
            <Route path="/dev/page/activity" element={<DevPagePreview path="/app/activity"><BackgroundActivity /></DevPagePreview>} />
            <Route path="/dev/page/creator/:platform/:handle" element={<DevPagePreview path="/app/creator/youtube/mkbhd"><CreatorProfile /></DevPagePreview>} />
          </>}
          {/* Auth. Bearri is invite-only: /login signs in, /waitlist is the way in for someone new, /invite is
              where an invite email lands (Clerk's sign-up, unlocked by the invite's ticket), and /signup is retired. */}
          <Route path="/login" element={<SignInPage />} />
          <Route path="/login/*" element={<SignInPage />} />
          <Route path="/waitlist" element={<WaitlistPage />} />
          <Route path="/invite" element={<InvitePage />} />
          <Route path="/invite/*" element={<InvitePage />} />
          <Route path="/signup" element={<SignupRedirect />} />
          <Route path="/signup/*" element={<SignupRedirect />} />

          {/* OAuth redirect URIs are registered upstream at `/oauth-callback`.
              Keeping this path at root avoids a provider-console change. */}
          <Route
            path="/oauth-callback"
            element={
              <AppGuard>
                <SubscriptionProvider>
                  <OAuthCallback />
                </SubscriptionProvider>
              </AppGuard>
            }
          />

          {/* ===================== AUTHED APP ===================== */}

          <Route
            path="/app"
            element={<Navigate to="/app/dashboard" replace />}
          />

          {/* The old full-bleed onboarding never rendered (the sign-up form sends people to the
              dashboard, where the three-step card does onboarding); its link stays a redirect. */}
          <Route path="/app/welcome" element={<Navigate to="/app/dashboard" replace />} />

          <Route
            path="/app/*"
            element={
              <AppGuard>
                <SubscriptionProvider>
                  <MainLayout>
                    {/* The page's code loads INSIDE the shell: the header, the lit folder tab and the
                        desk stay put, and the page's own pin is the first bear you see. Without this
                        boundary the app-wide fallback replaced the whole screen, header included,
                        with the old loading bear every time a tab was opened for the first time. */}
                    <Suspense fallback={<div className="flex min-h-[50vh] items-start justify-center pt-24"><NectarLoader size="lg" art={false} label="Loading page" /></div>}>
                    <Routes>
                      <Route path="dashboard" element={<Dashboard />} />
                      {/* Bearri: every deal and the agent that works them, one page. The Deals list and
                          the separate assistant page folded into it; their links land here. */}
                      <Route path="bearri" element={<Bearri />} />
                      <Route path="assistant" element={<ToBearri />} />
                      <Route path="discover" element={<SponsorIntel />} />
                      {/* Research has one URL. /app/sponsor-intel is the old name, kept as a redirect
                          so existing links and breadcrumbs keep working. */}
                      <Route path="sponsor-intel" element={<RedirectKeepQuery to="/app/discover" />} />
                      {/* "Browse" merged into the unified Search page — its
                          audience-size + active-deals filters now live there. */}
                      <Route
                        path="discover-creators"
                        element={<Navigate to="/app/discover" replace />}
                      />
                      {/* Rewards was a second creator search. Search is the one now; old links land there. */}
                      <Route path="support" element={<RedirectKeepQuery to="/app/discover" />} />
                      <Route path="profile" element={<Profile />} />
                      {/* Scout my channel is a Bearri tool now (?scout=). This address stays forever:
                          digest emails, saved notifications (?run=) and onboarding still point here. */}
                      <Route path="prospects" element={<ToScout />} />
                      <Route path="outreach" element={<ToBearri />} />
                      <Route path="activity" element={<BackgroundActivity />} />
                      {/* Offer & terms: one deal from the Bearri tab, opened up */}
                      <Route path="deals/:pitchId" element={<DealRoom />} />
                      <Route path="pitch-writer" element={<PitchWriter />} />
                      <Route
                        path="creator/:platform/:handle"
                        element={<CreatorProfile />}
                      />
                      {/* The Sponsors tab was a second copy of the dashboard's sponsors; there is one list now. */}
                      <Route path="codes" element={<Navigate to="/app/dashboard#sponsors" replace />} />
                      <Route path="codes/:codeId/performance" element={<CodeReportRedirect />} />
                      {/* Lists collapsed into Search's "View List" flag. */}
                      <Route path="lists" element={<Navigate to="/app/discover?list=1" replace />} />
                      {/* The old perk-offerings page is gone; its link lands on the dashboard. */}
                      <Route path="support-offerings" element={<Navigate to="/app/dashboard" replace />} />
                      {/* Plan & billing. The old plan page and the billing address both land on it, keeping
                          ?choose= / ?highlight= and #faq-chat. */}
                      <Route path="plan" element={<Plan />} />
                      <Route path="pricing" element={<RedirectKeepQuery to="/app/plan" />} />
                      <Route path="billing" element={<RedirectKeepQuery to="/app/plan" />} />

                      {/* Legacy within /app */}
                      <Route
                        path="followers"
                        element={<Navigate to="/app/dashboard" replace />}
                      />

                      {/* Catch-all inside /app → dashboard */}
                      <Route
                        path="*"
                        element={<Navigate to="/app/dashboard" replace />}
                      />
                    </Routes>
                    </Suspense>
                  </MainLayout>
                </SubscriptionProvider>
              </AppGuard>
            }
          />

          {/* ================= LEGACY ROOT REDIRECTS ================= */}
          {/* Old authed routes at root → /app equivalent. Preserves bookmarks
              and any Stripe success_url / OAuth return-url that still points
              at the pre-refactor paths. */}
          <Route
            path="/dashboard"
            element={<RedirectKeepQuery to="/app/dashboard" />}
          />
          <Route
            path="/discover"
            element={<RedirectKeepQuery to="/app/discover" />}
          />
          <Route
            path="/sponsor-intel"
            element={<RedirectKeepQuery to="/app/sponsor-intel" />}
          />
          <Route
            path="/support"
            element={<RedirectKeepQuery to="/app/discover" />}
          />
          <Route
            path="/profile"
            element={<RedirectKeepQuery to="/app/profile" />}
          />
          <Route
            path="/prospects"
            element={<RedirectKeepQuery to="/app/prospects" />}
          />
          <Route
            path="/pitch-writer"
            element={<RedirectKeepQuery to="/app/pitch-writer" />}
          />
          <Route path="/codes" element={<Navigate to="/app/dashboard#sponsors" replace />} />
          <Route
            path="/support-offerings"
            element={<RedirectKeepQuery to="/app/support-offerings" />}
          />
          <Route path="/pricing" element={<PricingDoor />} />
          <Route path="/welcome" element={<Navigate to="/app/dashboard" replace />} />
          <Route
            path="/followers"
            element={<RedirectKeepQuery to="/app/dashboard" />}
          />

          {/* Default catch-all: authed → dashboard, unauthed → landing */}
          <Route path="*" element={<SmartCatchAll />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
